import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import LeadForm from "./LeadForm";
import { useState, ReactNode } from "react";

interface LeadFormModalProps {
  trigger?: ReactNode;
  source?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  prefilledNeighborhood?: string;
  prefilledAddress?: string;
  modalTitle?: string;
  modalDescription?: string;
  isListingInquiry?: boolean; // If true, sends to both Brennan and Sebastian
}

export default function LeadFormModal({ 
  trigger, 
  source = "modal",
  isOpen,
  onOpenChange,
  prefilledNeighborhood,
  prefilledAddress,
  modalTitle,
  modalDescription,
  isListingInquiry = false
}: LeadFormModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const open = isOpen !== undefined ? isOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  // Close modal on successful form submission
  const handleSuccess = () => {
    setOpen(false);
  };

  // Determine the prefilled value for the address field
  const addressPrefill = prefilledAddress || prefilledNeighborhood;

  // If no trigger is provided and we're using controlled state, render without DialogTrigger
  if (!trigger && isOpen !== undefined) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0">
          <div className="p-6 md:p-8 space-y-6">
            <div className="space-y-2 text-center">
            <DialogTitle className="text-2xl font-serif font-bold text-[oklch(0.25_0.1_145)]">
              {modalTitle || "Let's Talk"}
            </DialogTitle>
            <DialogDescription className="text-[oklch(0.5_0.03_145)] text-sm">
              {modalDescription || (prefilledNeighborhood 
                ? `Selling in ${prefilledNeighborhood}? Let's talk.`
                : "Share a few details and we'll help you explore your options.")
                }
              </DialogDescription>
            </div>
            <LeadForm 
              source={source} 
              variant="inline" 
              prefilledNeighborhood={addressPrefill}
              onSuccess={handleSuccess}
              isListingInquiry={isListingInquiry}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-[oklch(0.75_0.15_85)] hover:bg-[oklch(0.65_0.15_85)] text-[oklch(0.2_0.03_145)] font-bold">
            Let&apos;s Talk
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0">
          <div className="p-6 md:p-8 space-y-6">
            <div className="space-y-2 text-center">
            <DialogTitle className="text-2xl font-serif font-bold text-[oklch(0.25_0.1_145)]">
              {modalTitle || "Let's Talk"}
            </DialogTitle>
            <DialogDescription className="text-[oklch(0.5_0.03_145)] text-sm">
              {modalDescription || (prefilledAddress 
                ? "Complete your details below."
                : "Share a few details and we'll help you explore your options.")
              }
            </DialogDescription>
          </div>
          <LeadForm 
            source={source} 
            variant="inline"
            prefilledNeighborhood={addressPrefill}
            onSuccess={handleSuccess}
            isListingInquiry={isListingInquiry}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
